// Ported from packages/workflow-engine-upstream/ui/src/AnswerPanel.tsx at commit bebc382. Changed from the
// reference: the inspector shows the plan version the step was picked in, and a source links only to
// a rendered (cited) evidence row of the same source and locator.
// Column 3: the answer with its evidence, and the step inspector for the selected step.
// Evidence ids and sources link to the evidence rows below the answer.
import type { EvidenceRow, RunState } from './api';
import { OPEN_LIST_MAX, summaryText, unknownLabel } from './answer-text';
import { stepStatus } from './PlanPanel';

function opSummary(op: Record<string, unknown>): string {
  const parts = [String(op.op)];
  if (Array.isArray(op.tags)) parts.push(op.tags.join(', '));
  if (op.aggregate) parts.push(String(op.aggregate));
  const period = op.period as { from: string; to: string } | undefined;
  if (period) parts.push(`${period.from.slice(0, 16)} → ${period.to.slice(0, 16)}`);
  if (op.query) parts.push(`"${String(op.query)}"`);
  if (op.question) parts.push(`"${String(op.question)}"`);
  if (op.relation) parts.push(`${JSON.stringify(op.from)} ${String(op.relation)}`);
  return parts.join(' · ');
}

/** A plan step as picked in the plan panel: its id and the version it was picked in. */
export interface PickedStep {
  id: string;
  version: number;
}

function Inspector({ s, picked }: { s: RunState; picked: PickedStep }) {
  const stepId = picked.id;
  const run = s.trace.steps.filter((x) => x.stepId === stepId && x.planVersion === picked.version).at(-1);
  const plan = s.trace.planVersions.find((p) => p.version === picked.version);
  const step = plan?.steps.find((x) => x.id === stepId);
  if (!step) return null;
  const evidence = s.trace.evidence.filter((e) => run?.evidenceIds?.includes(e.id));
  const calls = s.calls.filter((c) => c.stepId === stepId);
  const models = s.trace.modelCalls.filter((m) => m.stepId === stepId);
  return (
    <>
      <span className="h">Step inspector · {stepId}</span>
      <div className="kv">
        <span>Title</span>
        <span>{step.title}</span>
        <span>Kind</span>
        <span>{step.kind}</span>
        <span>Op</span>
        <span className="mono">{step.op ? opSummary(step.op as unknown as Record<string, unknown>) : step.bindings?.methodAssembly === 'true' ? 'Assemble the report from evidence' : step.bindings?.methodHuman === 'true' ? 'Human action required' : 'No tool operation'}</span>
        <span>Status</span>
        <span>{stepStatus(s, stepId)}</span>
        <span>Evidence</span>
        <span className="mono">
          {evidence.length
            ? evidence.map((e) => `${e.id}: ${valueText(e, '—')}${e.at ? ` at ${e.at.slice(0, 16)}` : ''}`).join('; ')
            : 'none'}
        </span>
        <span>Coverage</span>
        <span>{evidence.find((e) => e.coverage) ? evidence.filter((e) => e.coverage).map((e) => `${e.coverage!.observed} of ${e.coverage!.expected} samples`).join('; ') : '—'}</span>
        <span>Model calls</span>
        <span>{models.length ? `${models.length} · ${models.reduce((n, m) => n + m.durationMs, 0)} ms (${models.map((m) => m.purpose).join(', ')})` : 'none'}</span>
        <span>Tool calls</span>
        <span>{calls.length ? calls.map((c) => `#${c.seq} ${c.name} ${(c.output as { outcome?: string })?.outcome ?? (c.ok ? 'ok' : 'error')} · ${c.durationMs} ms`).join('; ') : 'none'}</span>
        <span>Bindings</span>
        <pre className="inspector-bindings">{JSON.stringify(step.bindings ?? {}, null, 2)}</pre>
      </div>
    </>
  );
}

/** A value with its unit; a yes-or-no verdict (above a limit, inside a range) has no unit. */
function valueText(e: EvidenceRow, missing: string): string {
  if (e.value === undefined || e.value === null) return missing;
  if (typeof e.value === 'boolean') return e.value ? 'yes' : 'no';
  return `${String(e.value)}${e.unit ? ` ${e.unit}` : ''}`;
}

const anchor = (id: string) => `ev-${id.replace(/[^\w-]/g, '_')}`;
const sourceText = (x: EvidenceRow['source']) => `${x.kind} ${x.ref}${x.locator ? ` (${x.locator})` : ''}`;

function Evidence({ rows }: { rows: EvidenceRow[] }) {
  if (!rows.length) return null;
  return (
    <details className="plan-bindings" open={rows.length <= OPEN_LIST_MAX}>
      <summary>Evidence rows ({rows.length})</summary>
      <div className="evidence">
        {rows.map((e) => (
          <div key={e.id} id={anchor(e.id)}>
            <b className="mono">{e.id}</b> {e.subject.display ?? e.subject.id}: {valueText(e, e.outcome)}
            {e.at ? ` at ${e.at.slice(0, 16)}` : ''} <span className="note">· {sourceText(e.source)}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

export function AnswerPanel({ s, step }: { s: RunState; step: PickedStep | null }) {
  const a = s.trace.answer;
  const rows = new Map(s.trace.evidence.map((e) => [e.id, e]));
  const cited = a ? [...new Set(a.findings.flatMap((f) => f.evidenceRefs))].map((id) => rows.get(id)).filter((e): e is EvidenceRow => !!e) : [];
  const subjects = (refs: string[]) => [...new Set(refs.map((id) => rows.get(id)).filter(Boolean).map((e) => e!.subject.display ?? e!.subject.id))];
  const rowFor = (src: { kind: string; ref: string; locator?: string }) =>
    cited.find((e) => e.source.kind === src.kind && e.source.ref === src.ref && (src.locator === undefined || e.source.locator === src.locator));
  const summary = a ? summaryText(a.headline, a.prose) : '';
  return (
    <div className="col">
      <span className="h">3 · Answer</span>
      {!a ? (
        <span className="note">The headline appears when the run finishes.</span>
      ) : (
        <>
          <b>{a.headline}</b>
          {summary && <p className="summary">{summary}</p>}
          {a.findings.length > 0 && (
            <details className="plan-bindings" open={a.findings.length <= OPEN_LIST_MAX}>
              <summary>Findings ({a.findings.length})</summary>
              {a.findings.map((f, i) => (
                <div key={f.id} className="find">
                  <span>
                    {f.rank ? `${f.rank}. ` : ''}
                    {f.statement}
                  </span>
                  <span className="ev">
                    finding {i + 1} ({f.id}) ·{' '}
                    {f.evidenceRefs.map((id, j) => (
                      <span key={id}>
                        {j ? ' · ' : ''}
                        <a className="ev" href={`#${anchor(id)}`}>
                          {id}
                        </a>
                      </span>
                    ))}
                    {subjects(f.evidenceRefs).length > 0 && ` · ${subjects(f.evidenceRefs).join(', ')}`}
                  </span>
                </div>
              ))}
            </details>
          )}
          {a.actions.map((x) => (
            <div key={x.id} className="find act">
              <span>Action: {x.statement}</span>
              <span className="ev">cites → {x.findingRefs.map((ref) => `finding ${a.findings.findIndex((f) => f.id === ref) + 1 || ref}`).join(', ')}</span>
            </div>
          ))}
          {a.unknowns.map((u, i) => (
            <div key={i} className="unk">
              <b>{unknownLabel(u.kind)}</b>: {u.detail}
            </div>
          ))}
          {a.sources.length > 0 && (
            <span className="note">
              Sources:{' '}
              {a.sources.map((x, i) => {
                const row = rowFor(x);
                return (
                  <span key={i}>
                    {i ? ' · ' : ''}
                    {row ? <a href={`#${anchor(row.id)}`}>{sourceText(x)}</a> : sourceText(x)}
                  </span>
                );
              })}
            </span>
          )}
          <Evidence rows={cited} />
        </>
      )}
      {step ? <Inspector s={s} picked={step} /> : <span className="note">Select a plan step to inspect it.</span>}
    </div>
  );
}
